import { EVENTS, subscribe } from 'ia-util-pubsub'
import { srcsetFor } from 'ia-util-helpers'

/* The `width` the card's Liquid asks for (ia-product-card.liquid); the swap
   must not out-request it. */
const CARD_SRC_WIDTH = 1000

/* IA Product Card — 2026 rework.
   - In-place variant swap from size chips / mini-card select: updates the buy
     form's variant id, the price inside the add-to-bag button and the card
     image (variants with no image of their own restore the card's default).
     No re-fetch.
   - Keeps the section-rendering re-render path on `variant:change` (PDP
     contexts publish it with fresh HTML).
   - The favorite heart is bound by Swym (ia-wishlist.js), not here. */
export class IaProductCard extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.sectionId = this.dataset.sectionId
    this.productId = this.dataset.productId

    this.variantChangeUnsubscriber = subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this))

    this.addEventListener('click', this.handleClick.bind(this), { signal })
    this.addEventListener('change', this.handleChange.bind(this), { signal })

    this.setupChipsFade(signal)
  }

  disconnectedCallback() {
    this.abortController.abort()
    this.variantChangeUnsubscriber?.()
    this.chipsResizeObserver?.disconnect()
  }

  /* Size-chip strip is a horizontal scroller. Show an edge fade only for the
     side that has chips scrolled off: none when everything fits, none at the
     end you've reached, both in the middle. */
  setupChipsFade(signal) {
    const scroller = this.querySelector('.ia-card-chips__list')
    const wrapper = scroller?.closest('.ia-card-chips')
    if (!scroller || !wrapper) return

    const update = () => {
      const overflow = scroller.scrollWidth - scroller.clientWidth
      const fits = overflow <= 1
      const atStart = scroller.scrollLeft <= 1
      const atEnd = scroller.scrollLeft >= overflow - 1
      wrapper.classList.toggle('has-fade-left', !fits && !atStart)
      wrapper.classList.toggle('has-fade-right', !fits && !atEnd)
    }

    scroller.addEventListener('scroll', update, { passive: true, signal })
    this.chipsResizeObserver = new ResizeObserver(update)
    this.chipsResizeObserver.observe(scroller)
    update()
  }

  handleClick(event) {
    const chip = event.target.closest('[data-card-chip]')
    if (chip) {
      this.selectVariant(chip.dataset)
      this.querySelectorAll('[data-card-chip]').forEach((el) => {
        const selected = el === chip
        el.classList.toggle('is-selected', selected)
        el.setAttribute('aria-pressed', String(selected))
      })
    }
  }

  handleChange(event) {
    const select = event.target.closest('[data-card-variant-select]')
    if (!select) return
    const option = select.options[select.selectedIndex]
    this.selectVariant({
      variantUnavailable: option.disabled ? '' : undefined,
      variantId: option.value,
      variantPrice: option.dataset.variantPrice,
      variantComparePrice: option.dataset.variantComparePrice,
      variantImage: option.dataset.variantImage,
      variantImageWidth: option.dataset.variantImageWidth,
    })
  }

  selectVariant({ variantId, variantPrice, variantComparePrice, variantImage, variantImageWidth, variantUnavailable }) {
    if (!variantId) return
    this.swapImage(variantImage, Number(variantImageWidth) || 0)

    const idInput = this.querySelector('form [name="id"]')
    if (idInput) idInput.value = variantId

    // A variant-locked card's links open the PDP on the picked size.
    if (this.hasAttribute('data-variant-links')) {
      this.querySelectorAll('a[href*="variant="]').forEach((link) => {
        const url = new URL(link.href)
        url.searchParams.set('variant', variantId)
        link.href = url.href
      })
    }

    // A sold-out pick takes the button with it and says so: the server would
    // refuse the add, and nothing on the card shows that refusal. The labels
    // ride the button (ia-product-buy), the plain span holds the text.
    const button = this.querySelector('form button[type="submit"]')
    if (button) {
      const unavailable = variantUnavailable !== undefined
      button.disabled = unavailable
      if (unavailable) button.setAttribute('aria-disabled', 'true')
      else button.removeAttribute('aria-disabled')
      const label = [...button.querySelectorAll('.ia-button__content > span')]
        .find((span) => !span.hasAttribute('aria-hidden') && !span.classList.contains('ia-button__divider'))
      if (label) {
        label.textContent = unavailable
          ? button.dataset.soldOutText || 'Out of Stock'
          : button.dataset.addToCartText || label.textContent
      }
    }

    const priceEl = this.querySelector('[data-sale-price]')
    if (priceEl && variantPrice) priceEl.textContent = variantPrice

    let compareEl = this.querySelector('[data-sale-price-compare]')
    if (variantComparePrice) {
      if (!compareEl && priceEl) {
        compareEl = document.createElement('strike')
        compareEl.setAttribute('data-sale-price-compare', '')
        priceEl.after(compareEl)
      }
      if (compareEl) {
        compareEl.textContent = variantComparePrice
        compareEl.hidden = false
      }
    } else if (compareEl) {
      compareEl.hidden = true
    }
  }

  /**
   * The card image follows the variant. A variant with no image of its own
   * restores the card's default, or the previous pick's picture would stick.
   * The srcset is rebuilt the way the snippet builds it: the ladder cut by
   * the file and closed by it; sizes already states the slot and stays.
   */
  swapImage(url, naturalWidth) {
    // Both card shapes, and the gallery's first slide is the one the variant
    // owns: the rest keep their own photos.
    const img = this.querySelector('.ia-product-card__image, .ia-card-mini__image')
    if (!img) return
    if (this.defaultImage === undefined) {
      this.defaultImage = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') }
    }
    if (!url) {
      img.src = this.defaultImage.src
      if (this.defaultImage.srcset) img.setAttribute('srcset', this.defaultImage.srcset)
      return
    }
    if (img.dataset.variantUrl === url) return
    img.dataset.variantUrl = url
    const srcset = srcsetFor(url, naturalWidth)
    if (srcset) img.setAttribute('srcset', srcset)
    const src = Math.min(CARD_SRC_WIDTH, naturalWidth || CARD_SRC_WIDTH)
    img.src = url + (url.includes('?') ? '&' : '?') + 'width=' + src
  }

  handleVariantChange({ detail }) {
    const { html, variant } = detail
    if (!html || !variant || detail.sectionId !== this.sectionId || detail.productId !== this.productId) return
    const newCard = html.querySelector('ia-product-card')
    if (!newCard) return
    this.outerHTML = newCard.outerHTML
  }
}

/* The card's later slides ship inert (ia-product-card.liquid). Once the page
   has loaded, every card near the screen builds them, and their pictures load
   two at a time at low priority: every second slide first (the desktop
   hover), then every third (the first arrow press after it), then the rest;
   cards further down join as they come near. A shopper
   who reaches for a gallery earlier builds it on the spot. One observer, one
   queue and one start for every card on the page. */
let pageLoaded = document.readyState === 'complete'
const nearScreen = new Set()
let nearObserver

const pictures = { next: [], third: [], rest: [], running: 0 }
const PICTURES_PARALLEL = 2
// A file that never answers frees its place in the queue after this long.
const PICTURE_TIMEOUT_MS = 10000

function loadPicture(img) {
  if (img.complete && img.naturalWidth) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, PICTURE_TIMEOUT_MS)
    const done = () => {
      clearTimeout(timer)
      resolve()
    }
    img.addEventListener('load', done, { once: true })
    img.addEventListener('error', done, { once: true })
    img.fetchPriority = 'low'
    img.loading = 'eager'
  })
}

function runPictures() {
  while (pictures.running < PICTURES_PARALLEL) {
    const img = pictures.next.shift() || pictures.third.shift() || pictures.rest.shift()
    if (!img) return
    pictures.running++
    loadPicture(img).finally(() => {
      pictures.running--
      runPictures()
    })
  }
}

function queuePictures(kind, slides) {
  slides.forEach((slide) => slide.querySelectorAll('img').forEach((img) => pictures[kind].push(img)))
  runPictures()
}

function buildWhenNear(media) {
  if (!pageLoaded) {
    nearScreen.add(media)
    return
  }
  nearObserver ||= new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      nearObserver.unobserve(entry.target)
      entry.target.buildAll({ fetch: true })
    })
  }, { rootMargin: '300px 0px' })
  nearObserver.observe(media)
}

// A page whose load never fires (a third-party file that hangs) starts anyway.
const LOAD_FALLBACK_MS = 4000

if (!pageLoaded) {
  const start = () => {
    if (pageLoaded) return
    pageLoaded = true
    nearScreen.forEach((media) => media.isConnected && buildWhenNear(media))
    nearScreen.clear()
  }
  window.addEventListener('load', () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 })
    else setTimeout(start, 200)
  }, { once: true })
  setTimeout(start, LOAD_FALLBACK_MS)
}

/* Scroll-snap mini gallery inside the card media region.
   Mobile: native swipe, progress bar via --ia-card-progress.
   Desktop: hover shows the 2nd image (Figma hover state), circle arrows step
   through slides; pointer leave reverts to the first image. */
export class IaCardMedia extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.track = this.querySelector('[data-card-track]')
    this.slideCount = parseInt(this.dataset.slideCount, 10) || 1
    this.style.setProperty('--ia-card-slides', this.slideCount)
    if (!this.track || this.slideCount < 2) return

    this.querySelector('[data-card-prev]')?.addEventListener('click', () => this.step(-1), { signal })
    this.querySelector('[data-card-next]')?.addEventListener('click', () => this.step(1), { signal })

    // Any reach for the gallery builds every slide before the gesture moves the
    // track: a hover, a touch or press, a wheel or trackpad swipe, focus.
    if (this.querySelector('template[data-card-slide-next], template[data-card-slides-rest]')) {
      const build = () => this.buildAll()
      this.addEventListener('pointerdown', build, { signal, once: true })
      this.addEventListener('touchstart', build, { signal, once: true, passive: true })
      this.addEventListener('focusin', build, { signal, once: true })
      this.track.addEventListener('wheel', build, { signal, once: true, passive: true })
      buildWhenNear(this)
    }

    this.hoverMedia = window.matchMedia('(hover: hover) and (min-width: 1024px)')

    this.track.addEventListener('scroll', this.handleScroll.bind(this), { passive: true, signal })

    /* Hide the progress line / arrows when the images already fit in one view
       (e.g. a 2-image product shown 2-up on a big card — nothing to scroll).
       Re-check on resize since the per-page count is layout-dependent. */
    this.syncPaged()
    this.pagedObserver = new ResizeObserver(this.syncPaged.bind(this))
    this.pagedObserver.observe(this.track)

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !this.hoverMedia.matches) return
      this.buildAll()
      this.goTo(1, 'auto')
    }, { signal })
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse' && this.hoverMedia.matches) this.goTo(0, 'auto')
    }, { signal })
  }

  disconnectedCallback() {
    this.abortController.abort()
    this.pagedObserver?.disconnect()
    nearObserver?.unobserve(this)
    nearScreen.delete(this)
    clearTimeout(this.settleTimer)
  }

  /* Builds the slides still waiting in their templates. `fetch`: their
     pictures are queued to load now, behind everything else, instead of when
     the track scrolls to them. */
  buildAll({ fetch = false } = {}) {
    const parts = ['next', 'rest'].map((kind) => {
      const template = this.querySelector(kind === 'next' ? 'template[data-card-slide-next]' : 'template[data-card-slides-rest]')
      if (!template) return null
      const slides = Array.from(template.content.children)
      this.track.append(template.content)
      template.remove()
      return { kind, slides }
    }).filter(Boolean)
    if (!parts.length) return
    if (fetch) {
      parts.forEach(({ kind, slides }) => {
        if (kind === 'rest') {
          queuePictures('third', slides.slice(0, 1))
          queuePictures('rest', slides.slice(1))
        } else {
          queuePictures(kind, slides)
        }
      })
    }
    this.syncPaged()
  }

  syncPaged() {
    /* Pages, not slides, drive the progress-bar thumb: a 2-up featured card
       shows 3-4 slides as 2 pages. Re-published on every track resize. */
    this.style.setProperty('--ia-card-pages', this.pages)
    this.classList.toggle('is-static', this.pages <= 1)
    this.syncSlideVisibility()
  }

  /* A slide only partly inside the scrollport is an artifact at rest: mandatory
     snapping always leaves the track on a page boundary, so the sliver is the
     clip edge sitting on a fractional device pixel (cards are sized by a
     calc() percentage, e.g. 376.5px wide — hence only some slider positions
     show it). Flag those so CSS can hide them outright instead of depending on
     subpixel rounding. Desktop only: a touch swipe shows partial slides by
     design, and its scroll position isn't ours to reason about. */
  syncSlideVisibility() {
    if (!this.hoverMedia?.matches) return
    const port = this.track.getBoundingClientRect()
    if (!port.width) return
    for (const slide of this.track.children) {
      const box = slide.getBoundingClientRect()
      // The overscan pixel puts a resting slide's right edge 1px past the port.
      const fullyInside = box.left >= port.left - 1 && box.right <= port.right + 2
      slide.classList.toggle('is-offscreen', !fullyInside)
    }
  }

  showAllSlides() {
    for (const slide of this.track.children) slide.classList.remove('is-offscreen')
  }

  handleScroll() {
    this.updateProgress()
    /* Resting between pages means a gesture is in flight (trackpad, or a smooth
       arrow scroll), and partial slides are legitimate there — reveal them and
       re-flag once the track settles back onto a page boundary. */
    const width = this.pageWidth
    if (width) {
      const offset = this.track.scrollLeft % width
      if (offset > 1 && offset < width - 1) this.showAllSlides()
    }
    clearTimeout(this.settleTimer)
    this.settleTimer = setTimeout(() => this.syncSlideVisibility(), 100)
  }

  /* A "page" is one slide, read from the live box: the slide is the track
     plus one overscan pixel (ia-product-card.css), so the snap points sit at
     slide multiples, not track multiples, and a fractional box (sliders size
     cards with a calc() percentage, e.g. 376.5px) stays exact rather than
     rounded like clientWidth. */
  get pageWidth() {
    const slide = this.track.firstElementChild
    return slide?.getBoundingClientRect().width || this.track.getBoundingClientRect().width || this.track.clientWidth
  }

  /* While slides still wait in their templates the track is short, so the
     count comes from the server's slide count and the slides one view holds:
     the arrows and the progress line are right before anything is built. */
  get pages() {
    const built = Math.max(1, Math.round(this.track.scrollWidth / this.pageWidth))
    if (this.track.children.length >= this.slideCount) return built
    const perView = Math.max(1, Math.round(this.track.clientWidth / this.pageWidth))
    return Math.max(built, Math.ceil(this.slideCount / perView))
  }

  get index() {
    return Math.round(this.track.scrollLeft / this.pageWidth)
  }

  step(direction) {
    this.buildAll()
    const next = Math.min(Math.max(this.index + direction, 0), this.pages - 1)
    /* Everything visible while the smooth scroll travels; handleScroll re-flags
       when it settles. */
    this.showAllSlides()
    this.goTo(next, 'smooth')
  }

  goTo(index, behavior) {
    this.track.scrollTo({ left: index * this.pageWidth, behavior })
    /* An instant scroll lands synchronously, so re-flag now rather than waiting
       on the settle timer — otherwise the sliver flashes for the hover state. */
    if (behavior !== 'smooth') this.syncSlideVisibility()
  }

  /* The page the track rests on, read off the slide width: the same number
     whether every slide is built or the track is still short. */
  updateProgress() {
    const width = this.pageWidth
    if (!width) return
    const progress = Math.min(this.track.scrollLeft / width, this.pages - 1)
    this.style.setProperty('--ia-card-progress', progress.toFixed(3))
  }
}

customElements.define('ia-product-card', IaProductCard)
customElements.define('ia-card-media', IaCardMedia)
